import { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const PromoCardTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [cards, setCards] = useState([]);

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const perms = user.adminRole?.permissions?.promoCards || {};
    const canCreate = isSuperAdmin || perms.create;
    const canEdit = isSuperAdmin || perms.edit;
    const canDelete = isSuperAdmin || perms.delete;

    const fetchCards = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/promo-cards');
            setCards(data.cards);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load promo cards", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCards();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteHandler = async (id) => {
        try {
            await axios.delete(`/api/v1/admin/promo-card/${id}`);
            enqueueSnackbar("Promo card deleted", { variant: "success" });
            setCards((prev) => prev.filter((c) => c._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete card", { variant: "error" });
        }
    };

    const columns = [
        {
            field: "image",
            headerName: "Image",
            minWidth: 100,
            flex: 0.4,
            sortable: false,
            renderCell: (params) => (
                params.row.image
                    ? <img draggable="false" src={params.row.image} alt={params.row.title} className="w-16 h-10 object-cover rounded" />
                    : <span className="text-xs text-gray-400">No image</span>
            ),
        },
        {
            field: "eyebrow",
            headerName: "Eyebrow",
            minWidth: 140,
            flex: 0.5,
        },
        {
            field: "title",
            headerName: "Title",
            minWidth: 180,
            flex: 1,
        },
        {
            field: "order",
            headerName: "Order",
            type: "number",
            minWidth: 80,
            flex: 0.3,
        },
        {
            field: "active",
            headerName: "Active",
            minWidth: 90,
            flex: 0.3,
            renderCell: (params) => (
                params.row.active
                    ? <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">Active</span>
                    : <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full text-xs font-medium">Hidden</span>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 100,
            flex: 0.3,
            sortable: false,
            renderCell: (params) => (
                <Actions editRoute={"promo-card"} deleteHandler={deleteHandler} id={params.row.id} name={params.row.title} canEdit={canEdit} canDelete={canDelete} />
            ),
        },
    ];

    const rows = cards.map((card) => ({
        id: card._id,
        eyebrow: card.eyebrow,
        title: card.adminLabel || card.title || "Untitled card",
        image: card.image?.url,
        order: card.order,
        active: card.active,
    }));

    return (
        <>
            <MetaData title="Admin: Promo Cards | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <div>
                    <Link to="/admin/site-content" className="text-sm text-primary-blue hover:underline">&larr; Back to Site Content</Link>
                    <h1 className="text-lg font-medium uppercase mt-1">Promo Cards</h1>
                </div>
                {canCreate && (
                    <Link to="/admin/promo-card/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Card</Link>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full" style={{ height: 470 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={10}
                    disableSelectIconOnClick
                    sx={{ boxShadow: 0, border: 0 }}
                />
            </div>
        </>
    );
};

export default PromoCardTable;